import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import { DownloadButton } from "./DownloadButton";
import { listDocuments, getDocument } from "./api";
import {
  documentPdfUrl,
  documentExcelUrl,
  type DocumentSummary,
  type DocumentType,
} from "./types";
import type { ParsedExtraction } from "@/features/upload/types";
import type { PaaExtraction } from "@/features/paa/types";
import { useWhatChangedEnabled } from "@/features/paa/flags";

// =============================================================================
// History — every run (objections + PAA), newest first. Clicking a row opens
// its saved content in a dialog; the exact PDF that was delivered (and a
// regenerated Excel for objection runs) download from the archive.
// =============================================================================

export function HistoryPage() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<DocumentSummary | null>(null);

  // Debounce typing → server query.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const docs = useQuery({
    queryKey: ["documents", query],
    queryFn: ({ signal }) => listDocuments(query, 50, signal),
    // A run saves a draft as soon as it's processed — always refetch on open and
    // poll, so a run you just did shows up without a manual reload.
    refetchOnMount: "always",
    refetchInterval: 30_000,
  });

  return (
    <div className="animate-reveal">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label mb-2">Completed work</div>
          <h1 className="text-[clamp(2.25rem,4.5vw,3.25rem)] font-medium tracking-tight text-ink">
            History
          </h1>
        </div>
        <div className="w-full max-w-xs">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search address, job # or file…"
            aria-label="Search documents"
          />
        </div>
      </div>

      <section className="rounded-lg border border-rule bg-surface shadow-soft">
        <header className="flex items-baseline justify-between gap-4 border-b border-rule px-6 py-3.5">
          <div className="text-[0.95rem] font-semibold text-ink">
            Generated documents
          </div>
          <div className="text-[0.82rem] text-ink-muted">
            {docs.data ? `${docs.data.length} shown` : "—"}
          </div>
        </header>

        {docs.isLoading ? (
          <div className="px-6 py-10 text-center text-[0.92rem] text-ink-muted">
            Loading…
          </div>
        ) : docs.isError ? (
          <div className="px-6 py-10 text-center text-[0.92rem] text-danger">
            Couldn't load history. Try refreshing.
          </div>
        ) : !docs.data || docs.data.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <div className="label mb-1">
              {query ? "No matches" : "Nothing here yet"}
            </div>
            <p className="text-[0.92rem] text-ink-muted">
              {query
                ? "Try a different address, job number or filename."
                : "Runs land here automatically as soon as a file is processed — as a draft until you generate the PDF."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-rule">
            {docs.data.map((doc) => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                onOpen={() => setSelected(doc)}
              />
            ))}
          </ul>
        )}
      </section>

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={
          selected && (
            <div className="flex flex-wrap items-center gap-3">
              <TypeChip type={selected.type} />
              <span>{selected.address?.trim() || selected.fileName}</span>
              {!selected.generatedAt && <Badge tint="butter">Draft</Badge>}
            </div>
          )
        }
      >
        {selected && <DocumentDetailDialogBody doc={selected} />}
      </Dialog>
    </div>
  );
}

// =============================================================================
// Row + detail dialog
// =============================================================================

function DocumentRow({
  doc,
  onOpen,
}: {
  doc: DocumentSummary;
  onOpen: () => void;
}) {
  return (
    <li>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-4 transition-colors hover:bg-canvas-2/40">
        <TypeChip type={doc.type} />

        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          aria-haspopup="dialog"
        >
          <div className="truncate text-[0.95rem] font-medium text-ink">
            {doc.address?.trim() || doc.fileName}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[0.82rem] text-ink-muted">
            {doc.jobNumber?.trim() && (
              <span className="font-mono">{doc.jobNumber}</span>
            )}
            <span>
              {doc.type === "Objections"
                ? `${doc.itemCount} objection${doc.itemCount === 1 ? "" : "s"}${
                    doc.resolvedCount ? ` · ${doc.resolvedCount} resolved` : ""
                  }`
                : `${doc.itemCount} sheet${doc.itemCount === 1 ? "" : "s"}`}
            </span>
            {doc.userId && <span>{doc.userId}</span>}
          </div>
        </button>

        {!doc.generatedAt && <Badge tint="butter">Draft</Badge>}

        <span className="text-[0.82rem] text-ink-muted">
          {fmtWhen(doc.updatedAt)}
        </span>

        {doc.type === "Objections" && (
          <DownloadButton url={documentExcelUrl(doc)}>Excel</DownloadButton>
        )}
        <DownloadButton url={documentPdfUrl(doc)} filename={doc.fileName}>
          PDF
        </DownloadButton>
      </div>
    </li>
  );
}

function DocumentDetailDialogBody({ doc }: { doc: DocumentSummary }) {
  const detail = useQuery({
    queryKey: ["documents", doc.type, doc.id],
    queryFn: ({ signal }) => getDocument(doc.type, doc.id, signal),
  });

  return (
    <>
      <div className="min-h-[8rem] flex-1 overflow-y-auto px-6 py-5">
        {detail.isLoading ? (
          <div className="text-[0.88rem] text-ink-muted">Loading…</div>
        ) : detail.isError || !detail.data ? (
          <div className="text-[0.88rem] text-danger">
            Couldn't load this document.
          </div>
        ) : doc.type === "Objections" ? (
          <ObjectionsDetail payload={detail.data.payload as ParsedExtraction} />
        ) : (
          <PaaDetail payload={detail.data.payload as PaaExtraction} />
        )}
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-rule px-6 py-3.5">
        <span className="text-[0.82rem] text-ink-muted">
          {doc.userId ? `${doc.userId} · ` : ""}last updated {fmtWhen(doc.updatedAt)}
        </span>
        <div className="flex items-center gap-2">
          {doc.type === "Objections" && (
            <DownloadButton url={documentExcelUrl(doc)}>
              Download Excel
            </DownloadButton>
          )}
          <DownloadButton url={documentPdfUrl(doc)} filename={doc.fileName}>
            Download PDF
          </DownloadButton>
        </div>
      </footer>
    </>
  );
}

function ObjectionsDetail({ payload }: { payload: ParsedExtraction }) {
  return (
    <div className="space-y-4">
      <PropertyFacts
        facts={[
          ["Address", payload.address],
          ["Job #", payload.jobNumber],
          ["Borough", payload.borough],
          ["BIN", payload.bin],
          ["Block / Lot", joinNonEmpty([payload.block, payload.lot], " / ")],
          ["Applicant", payload.applicantName],
        ]}
      />
      <ol className="space-y-2.5">
        {payload.objections.map((o, i) => (
          <li key={o.id} className="flex gap-3">
            <span className="mt-0.5 font-mono text-[0.8rem] text-ink-faint">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="min-w-0">
              <p
                className={cn(
                  "text-[0.9rem] leading-relaxed text-ink",
                  o.resolved && "text-ink-faint line-through",
                )}
              >
                {o.objectionText}
              </p>
              <div className="mt-1 flex flex-wrap gap-2 text-[0.76rem]">
                {o.resolved && <Badge tint="sage">Resolved</Badge>}
                {o.assignedTo && <Badge tint="sky">{o.assignedTo}</Badge>}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function PaaDetail({ payload }: { payload: PaaExtraction }) {
  const whatChanged = useWhatChangedEnabled();
  return (
    <div className="space-y-4">
      <PropertyFacts
        facts={[
          ["Address", payload.address],
          ["Job #", payload.jobNumber],
          ["Borough", payload.borough],
          ["BIN", payload.bin],
          ["Block / Lot", joinNonEmpty([payload.block, payload.lot], " / ")],
          ["Applicant", payload.applicantName],
        ]}
      />
      <table className="w-full text-[0.88rem]">
        <thead className="text-[0.76rem] font-semibold uppercase tracking-wider text-ink-muted">
          <tr className="text-left">
            <th className="pb-2 pr-4 font-semibold">Sheet</th>
            <th className="pb-2 pr-4 font-semibold">Rev</th>
            <th className="pb-2 pr-4 font-semibold">Action</th>
            <th className="pb-2 pr-4 font-semibold">Page ID</th>
            <th className="pb-2 pr-4 font-semibold">Superseding</th>
            {whatChanged && (
              <th className="pb-2 font-semibold">What changed</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-rule">
          {payload.rows
            .filter((r) => r.sheetId.trim())
            .map((r) => (
              <tr key={r.id}>
                <td className="py-2 pr-4 font-mono">{r.sheetId}</td>
                <td className="py-2 pr-4 font-mono">{r.revision}</td>
                <td className="py-2 pr-4">{actionLabel(r.action)}</td>
                <td className="py-2 pr-4 font-mono">
                  {r.originalNewOmitPageId || "—"}
                </td>
                <td className="py-2 pr-4 font-mono">
                  {r.supersedingPageId || "—"}
                </td>
                {whatChanged && (
                  <td className="py-2 text-ink-soft">{r.changeNote || "—"}</td>
                )}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Small bits
// =============================================================================

function TypeChip({ type }: { type: DocumentType }) {
  return (
    <Badge tint={type === "Objections" ? "primary" : "lilac"}>
      {type === "Objections" ? "Objections" : "PAA"}
    </Badge>
  );
}

function Badge({
  tint,
  children,
}: {
  tint: "primary" | "sage" | "sky" | "lilac" | "butter";
  children: React.ReactNode;
}) {
  const tints = {
    primary: "bg-primary-soft text-primary-ink",
    sage: "bg-sage-soft text-sage-ink",
    sky: "bg-sky-soft text-sky-ink",
    lilac: "bg-lilac-soft text-lilac-ink",
    butter: "bg-butter-soft text-butter-ink",
  } as const;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.76rem] font-medium",
        tints[tint],
      )}
    >
      {children}
    </span>
  );
}

function PropertyFacts({ facts }: { facts: [string, string | null | undefined][] }) {
  const present = facts.filter(([, v]) => v?.trim());
  if (present.length === 0) return null;
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-1.5 text-[0.85rem]">
      {present.map(([label, value]) => (
        <div key={label} className="flex gap-1.5">
          <dt className="text-ink-muted">{label}:</dt>
          <dd className="font-medium text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function joinNonEmpty(parts: (string | null | undefined)[], sep: string) {
  const present = parts.filter((p) => p?.trim());
  return present.length ? present.join(sep) : null;
}

function actionLabel(action: string): string {
  switch (action) {
    case "N":
      return "New";
    case "S":
      return "Superseding";
    case "O":
      return "Omit";
    default:
      return action;
  }
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
